import { getAllApiUrls } from '../config/apiConfig';
import axios from 'axios';

const tryRequestWithFallback = async (requestFn, isPublic = false) => {
  const urls = getAllApiUrls();
  const token = localStorage.getItem('token');
  
  if (!Array.isArray(urls) || urls.length === 0) {
    throw new Error('No API URLs available');
  }
  
  const validUrls = urls.filter(url => url && typeof url === 'string');
  
  if (validUrls.length === 0) {
    throw new Error('No valid API URLs available');
  }
  
  let lastError = null;
  
  for (let i = 0; i < validUrls.length; i++) {
    const baseURL = validUrls[i];
    const isLast = i === validUrls.length - 1;
    
    try {
      const client = axios.create({
        baseURL,
        timeout: 8000,
        headers: {
          'Content-Type': 'application/json',
          ...(token && !isPublic ? { Authorization: `Bearer ${token}` } : {})
        }
      });
      
      const result = await requestFn(client);
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`✅ Connected to API: ${baseURL}`);
      }
      
      return result;
    } catch (error) {
      lastError = error;
      
      if (process.env.NODE_ENV === 'development' && !isLast) {
        console.log(`⚠️ Failed to connect to ${baseURL}, trying next URL...`);
      }
      
      if (isLast) {
        throw error;
      }
      continue;
    }
  }
  
  if (lastError) {
    throw lastError;
  }
  
  throw new Error('Failed to connect to any API URL');
};

const musicService = {
  getAllTracks: async (params = {}) => {
    try {
      const res = await tryRequestWithFallback(
        (client) => client.get('/MusicTracks', { params }),
        false
      );
      return res?.data || [];
    } catch (error) {
      if (error.response?.status === 404 || error.code === 'ECONNREFUSED') {
        return [];
      }
      throw error;
    }
  },

  getTrackById: async (id) => {
    try {
      const res = await tryRequestWithFallback(
        (client) => client.get(`/MusicTracks/${id}`),
        false
      );
      return res?.data;
    } catch (error) {
      if (error.response?.status === 404 || error.code === 'ECONNREFUSED') {
        return null;
      }
      throw error;
    }
  },

  getTracksByCategory: async (categoryId) => {
    try {
      const res = await tryRequestWithFallback(
        (client) => client.get(`/MusicTracks/category/${categoryId}`),
        false
      );
      return res?.data || [];
    } catch (error) {
      if (error.response?.status === 404 || error.code === 'ECONNREFUSED') {
        return [];
      }
      throw error;
    }
  },

  getTracksByPlatform: async (platformId) => {
    try {
      const res = await tryRequestWithFallback(
        (client) => client.get(`/MusicTracks/platform/${platformId}`),
        false
      );
      return res?.data || [];
    } catch (error) {
      if (error.response?.status === 404 || error.code === 'ECONNREFUSED') {
        return [];
      }
      throw error;
    }
  },

  getTracksByCover: async (cover) => {
    try {
      const res = await tryRequestWithFallback(
        (client) => client.get(`/MusicTracks/cover/${cover}`),
        false
      );
      return res?.data || [];
    } catch (error) {
      if (error.response?.status === 404 || error.code === 'ECONNREFUSED') {
        return [];
      }
      throw error;
    }
  },

  incrementPlayCount: async (trackId) => {
    try {
      const res = await tryRequestWithFallback(
        (client) => client.post(`/MusicTracks/${trackId}/play`),
        false
      );
      return res?.data;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to increment play count:', error);
      }
      return null;
    }
  },

  getRecommendations: async (limit = 10, excludeRecent = true, recentDays = 7) => {
    try {
      const res = await tryRequestWithFallback(
        (client) => client.get('/UserMusicRecommendations/suggestions', {
          params: { limit, excludeRecent, recentDays }
        }),
        false
      );
      return res?.data || [];
    } catch (error) {
      if (error.response?.status === 404 || error.code === 'ECONNREFUSED') {
        return [];
      }
      throw error;
    }
  },

  getRecommendationHistory: async (limit = 20) => {
    try {
      const res = await tryRequestWithFallback(
        (client) => client.get('/UserMusicRecommendations', {
          params: { limit }
        }),
        false
      );
      return res?.data || [];
    } catch (error) {
      if (error.response?.status === 404 || error.code === 'ECONNREFUSED') {
        return [];
      }
      throw error;
    }
  },

  addRecommendation: async (trackId, score = 0.00) => {
    try {
      const res = await tryRequestWithFallback(
        (client) => client.post('/UserMusicRecommendations', {
          musicTrackId: trackId,
          score
        }),
        false
      );
      return res?.data;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to add recommendation:', error);
      }
      throw error;
    }
  },

  updateInteraction: async (recommendationId, interaction) => {
    try {
      const res = await tryRequestWithFallback(
        (client) => client.put(`/UserMusicRecommendations/${recommendationId}/interaction`, interaction),
        false
      );
      return res?.data;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to update interaction:', error);
      }
      throw error;
    }
  },

  getFavorites: async () => {
    try {
      const res = await tryRequestWithFallback(
        (client) => client.get('/UserFavoriteMusic'),
        false
      );
      return res?.data || [];
    } catch (error) {
      if (error.response?.status === 404 || error.code === 'ECONNREFUSED') {
        return [];
      }
      if (error.response?.status === 401) {
        return [];
      }
      throw error;
    }
  },

  isFavorited: async (trackId) => {
    try {
      const res = await tryRequestWithFallback(
        (client) => client.get(`/UserFavoriteMusic/check/${trackId}`),
        false
      );
      return res?.data?.isFavorite || false;
    } catch (error) {
      if (error.response?.status === 404 || error.code === 'ECONNREFUSED' || error.response?.status === 401) {
        return false;
      }
      if (process.env.NODE_ENV === 'development') {
        console.error('Error checking favorite status:', error);
      }
      return false;
    }
  },

  addToFavorites: async (trackId) => {
    try {
      const res = await tryRequestWithFallback(
        (client) => client.post(`/UserFavoriteMusic/${trackId}`),
        false
      );
      return res?.data;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to add to favorites:', error);
      }
      throw error;
    }
  },

  removeFromFavorites: async (trackId) => {
    try {
      const res = await tryRequestWithFallback(
        (client) => client.delete(`/UserFavoriteMusic/${trackId}`),
        false
      );
      return res?.data;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to remove from favorites:', error);
      }
      throw error;
    }
  },

  toggleFavorite: async (trackId) => {
    try {
      const res = await tryRequestWithFallback(
        (client) => client.post(`/UserFavoriteMusic/${trackId}/toggle`),
        false
      );
      return res?.data;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to toggle favorite:', error);
      }
      throw error;
    }
  },

  getCategories: async () => {
    try {
      const res = await tryRequestWithFallback(
        (client) => client.get('/MusicCategories'),
        true
      );
      return res?.data || [];
    } catch (error) {
      if (error.response?.status === 404 || error.code === 'ECONNREFUSED') {
        return [];
      }
      throw error;
    }
  },

  getCategoryById: async (id) => {
    try {
      const res = await tryRequestWithFallback(
        (client) => client.get(`/MusicCategories/${id}`),
        true
      );
      return res?.data;
    } catch (error) {
      if (error.response?.status === 404 || error.code === 'ECONNREFUSED') {
        return null;
      }
      throw error;
    }
  },

  getPlatforms: async () => {
    try {
      const res = await tryRequestWithFallback(
        (client) => client.get('/MusicPlatforms'),
        true
      );
      return res?.data || [];
    } catch (error) {
      if (error.response?.status === 404 || error.code === 'ECONNREFUSED') {
        return [];
      }
      throw error;
    }
  },

  getPlatformById: async (id) => {
    try {
      const res = await tryRequestWithFallback(
        (client) => client.get(`/MusicPlatforms/${id}`),
        true
      );
      return res?.data;
    } catch (error) {
      if (error.response?.status === 404 || error.code === 'ECONNREFUSED') {
        return null;
      }
      throw error;
    }
  },

  getPreferences: async () => {
    try {
      const res = await tryRequestWithFallback(
        (client) => client.get('/UserMusicPreferences'),
        false
      );
      return res?.data;
    } catch (error) {
      if (error.response?.status === 404 || error.code === 'ECONNREFUSED' || error.response?.status === 401) {
        return null;
      }
      throw error;
    }
  },

  savePreferences: async (preferences) => {
    try {
      const res = await tryRequestWithFallback(
        (client) => client.post('/UserMusicPreferences', preferences),
        false
      );
      return res?.data;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to save preferences:', error);
      }
      throw error;
    }
  },

  searchTracks: async (query, filters = {}) => {
    try {
      const params = { q: query, ...filters };
      const res = await tryRequestWithFallback(
        (client) => client.get('/MusicTracks', { params }),
        false
      );
      return res?.data || [];
    } catch (error) {
      if (error.response?.status === 404 || error.code === 'ECONNREFUSED') {
        return [];
      }
      throw error;
    }
  },

  recordInteraction: async (recommendationId, interaction) => {
    try {
      const res = await tryRequestWithFallback(
        (client) => client.put(`/UserMusicRecommendations/${recommendationId}/interaction`, interaction),
        false
      );
      return res?.data;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to record interaction:', error);
      }
      throw error;
    }
  }
};

export { musicService };
export default musicService;
